import type { IntelReportCall } from '@einhaus/project314-types';
import { callOutcomeLabel, eastern, sourceLink, targetLabel, words } from './reportModel';
const cell = 'border-t border-slate-800 px-3 py-3 text-left align-top';

export function CallRow({ call }: { call: IntelReportCall }) {
    const link = sourceLink(call.sourceUrl);
    const result = call.outcome?.directionalExcessPercent;
    return (
        <tr>
            <td className={`${cell} whitespace-nowrap`}>
                <div>{eastern(call.availableAt)}</div>
                <div className="mt-1 text-xs text-slate-400">{call.captureMode === 'live' ? 'Captured live' : 'Historical backfill'}</div>
            </td>
            <td className={cell}>
                <div className="font-semibold">{call.symbol ?? `${call.entityType} #${call.entityId}`}</div>
                <div className="text-slate-400">
                    {words(call.instrumentForm)} · {call.underlyingBias ?? 'direction unclear'}
                </div>
                {call.instrumentForm === 'option' && (
                    <div className="text-xs text-slate-400">
                        {call.strikePrice ?? 'Strike unstated'} {call.optionType ?? 'type unstated'} ·{' '}
                        {call.expirationDate ?? 'expiry unstated'}
                    </div>
                )}
                <div className="text-xs text-slate-400">
                    {words(call.statementKind)} · {words(call.action)}
                </div>
            </td>
            <td className={`${cell} min-w-72 max-w-xl`}>
                <blockquote className="whitespace-pre-wrap">{call.evidenceQuote}</blockquote>
                <div className="mt-2 flex gap-3 text-xs text-slate-400">
                    {link && (
                        <a className="text-sky-300 underline" href={link} target="_blank" rel="noreferrer">
                            Source post
                        </a>
                    )}
                    <span>
                        Call #{call.eventId}
                        {call.evidenceSource === 'image' ? ' · image text' : ''}
                    </span>
                </div>
                {call.conditionKind && (
                    <p className="mt-2 text-amber-200">
                        Condition: {words(call.conditionKind)}
                        {call.conditionPrice === null ? '' : ` ${call.conditionPrice}`}
                    </p>
                )}
                {call.targets.length > 0 && (
                    <details className="mt-3">
                        <summary className="cursor-pointer text-sky-300">
                            {call.targets.length} stated price level{call.targets.length === 1 ? '' : 's'}
                        </summary>
                        <ul className="mt-2 space-y-2">
                            {call.targets.map((target) => (
                                <li key={target.id}>
                                    <div>{targetLabel(target)}</div>
                                    {target.targetRole === 'price_target' && (
                                        <div className="text-xs text-slate-400">
                                            {target.claimRank ? `${words(target.claimRank)} · ` : ''}
                                            {target.evaluationStatus ? words(target.evaluationStatus) : 'Awaiting evaluation'}
                                            {target.notEvaluatedReason ? ` (${words(target.notEvaluatedReason)})` : ''}
                                            {target.deadlineSessionDate ? ` · deadline ${target.deadlineSessionDate}` : ''}
                                            {target.resolvedSessionDate ? ` · resolved ${target.resolvedSessionDate}` : ''}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </td>
            <td className={`${cell} ${result == null ? 'text-slate-400' : result > 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                {callOutcomeLabel(call)}
                {call.outcome?.entrySessionDate && (
                    <div className="mt-1 text-xs text-slate-400">
                        Entry {call.outcome.entrySessionDate}
                        {call.outcome.exitSessionDate ? ` · exit ${call.outcome.exitSessionDate}` : ''}
                    </div>
                )}
                {call.instrumentForm === 'option' && <div className="text-xs text-slate-400">Underlying stock only</div>}
            </td>
        </tr>
    );
}
